<template>
    <Layout>
        <PageHeader :title="$t('module.document')" :items="breadcrumbs" />
        <div class="card">
            <div class="card-body">
                <div class="row mb-3">
                    <div class="col-lg-4 mb-2 mb-lg-0">
                        <b-input-group>
                            <b-form-input v-model.trim="searchText" type="search" :placeholder="$t('label.search')"
                                autocomplete="off" @keyup.enter="applySearch" />
                            <b-input-group-append>
                                <b-button variant="info" @click="applySearch">
                                    <i class="mdi mdi-magnify"></i>
                                </b-button>
                            </b-input-group-append>
                        </b-input-group>
                    </div>
                    <div class="col-lg-8 d-flex justify-content-lg-end">
                        <b-button variant="info" @click="openForm">
                            <i class="mdi mdi-plus mr-1"></i>{{ $t('label.createX', [$t('module.document')]) }}
                        </b-button>
                    </div>
                </div>

                <div v-if="!status.list" class="d-flex justify-content-center p-5">
                    <b-spinner variant="info" :label="$t('label.loading')"></b-spinner>
                </div>
                <template v-else>
                    <b-table :items="tableRows" :fields="fields" show-empty responsive no-local-sorting
                        @sort-changed="changeSort">
                        <template #cell(fileName)="{ item }">
                            <a v-if="documentTarget(item)" :href="documentTarget(item)" target="_blank"
                                rel="noopener noreferrer" class="text-info" v-b-tooltip.hover
                                :title="$t(item.isInternal ? 'label.openDocument' : 'label.openExternalLink')">
                                <i :class="item.isInternal ? 'mdi-file-document-outline' : 'mdi-open-in-new'"
                                    class="mdi font-size-18"></i>
                            </a>
                            <span v-else class="text-muted" v-b-tooltip.hover :title="$t('label.documentUnavailable')">
                                <i class="mdi mdi-file-remove-outline font-size-18"></i>
                            </span>
                        </template>
                        <template #cell(companyName)="{ item }">
                            <CompanyLink :uuid="item.visibleToCompanyUUID" :name="item.companyName" />
                        </template>
                        <template #cell(isActive)="{ item }">
                            <b-badge :variant="item.isActive ? 'success' : 'secondary'">
                                {{ item.isActive ? $t('label.active') : $t('label.inactive') }}
                            </b-badge>
                        </template>
                        <template #cell(isInternal)="{ item }">
                            <b-badge :variant="item.isInternal ? 'info' : 'secondary'">
                                {{ $t(item.isInternal ? 'label.ihextarApp' : 'label.external') }}
                            </b-badge>
                        </template>
                        <template #cell(actions)="{ item }">
                            <a href="javascript:void(0);" class="text-info" v-b-tooltip.hover
                                :title="$t('label.updateX', [$t('module.document')])" @click.stop="openForm(item)">
                                <i class="mdi mdi-pencil-outline font-size-18"></i>
                            </a>
                            <a href="javascript:void(0);" class="text-danger ml-2" v-b-tooltip.hover
                                :title="$t('label.deleteX', [$t('module.document')])" @click.stop="confirmDelete(item)">
                                <i class="mdi mdi-delete-outline font-size-18"></i>
                            </a>
                        </template>
                    </b-table>

                    <div v-if="pagination.total"
                        class="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3">
                        <small class="text-muted mb-2 mb-md-0">
                            {{ $tc('label.results', pagination.total, [pagination.total]) }}
                        </small>
                        <b-pagination v-model="currentPage" :total-rows="pagination.total"
                            :per-page="searchCriteria.pageSize" class="mb-0" @change="pageNavigation" />
                    </div>
                </template>
            </div>
        </div>
        <b-modal id="document-form"
            :title="$t(document.uuid ? 'label.updateX' : 'label.createX', [$t('module.document')])" size="lg" hide-footer
            hide-header-close centered @hidden="closeForm">
            <b-container fluid>
                <DocumentForm v-if="formVisible" :document-u-u-i-d="document.uuid"
                    :search-criteria="searchCriteria" @done="closeForm" />
            </b-container>
        </b-modal>
    </Layout>
</template>

<script>
import { mapState } from 'vuex';
import Layout from '@/views/layouts/main';
import PageHeader from '@/components/page-header';
import i18n from '@/i18n';
import { SESSION } from '@/constants';
import { sessionHelper } from '@/utils';
import { documentMethods } from '@/state/api';
import { DOCUMENT_MODULE } from '@/state/api/document.state';
import DocumentForm from '@/views/pages/admin/document/form';

const defaultCriteria = () => ({
    pageNumber: 1,
    pageSize: 15,
    searchArray: [],
    sortBy: 'created_at',
    orderBy: 'desc',
});

export default {
    name: 'DocumentListing',
    page: { title: i18n.t('module.document') },
    components: { Layout, PageHeader, DocumentForm },
    data() {
        return {
            searchText: '',
            currentPage: 1,
            formVisible: false,
            document: { uuid: '' },
            searchCriteria: defaultCriteria(),
            breadcrumbs: [
                { text: i18n.t('module.communication'), href: '/communications' },
                { text: i18n.t('module.document'), active: true },
            ],
            fields: [
                {
                    key: 'name',
                    label: i18n.t('label.name'),
                    sortable: true,
                    class: 'document-name-column',
                    thStyle: { width: '400px' },
                },
                { key: 'fileName', label: i18n.t('label.file'), sortable: false, class: 'text-nowrap' },
                { key: 'companyName', label: i18n.t('label.company'), sortable: false, class: 'document-company-column', thStyle: { width: '100px' } },
                { key: 'isInternal', label: i18n.t('label.openIn'), sortable: false, class: 'text-nowrap' },
                { key: 'isActive', label: i18n.t('label.status'), sortable: false, class: 'text-nowrap' },
                { key: 'createdAt', label: i18n.t('label.createdAt'), sortable: true, class: 'text-nowrap' },
                { key: 'actions', label: i18n.t('label.action'), sortable: false, class: 'text-nowrap' },
            ],
        };
    },
    computed: {
        ...mapState(DOCUMENT_MODULE, ['status']),
        tableRows() {
            return this.$store.state.api.document.list.data || [];
        },
        pagination() {
            return this.$store.state.api.document.list.pagination || {};
        },
    },
    created() {
        this.restoreSearch();
        this.loadDocuments();
    },
    methods: {
        ...documentMethods,
        restoreSearch() {
            if (sessionHelper.hasSearchCriteria(SESSION.DOCUMENT_SEARCH)) {
                this.searchCriteria = {
                    ...defaultCriteria(),
                    ...sessionHelper.retrieveSearch(SESSION.DOCUMENT_SEARCH),
                };
            }
            this.currentPage = this.searchCriteria.pageNumber;
            this.searchText = this.searchCriteria.searchArray[0] || '';
        },
        async loadDocuments() {
            sessionHelper.storeSearch(SESSION.DOCUMENT_SEARCH, this.searchCriteria);
            await this.getAllDocument({ filter: this.searchCriteria });
        },
        applySearch() {
            this.currentPage = 1;
            this.searchCriteria = {
                ...this.searchCriteria,
                pageNumber: 1,
                searchArray: this.searchText ? [this.searchText] : [],
            };
            this.loadDocuments();
        },
        pageNavigation(pageNumber) {
            this.currentPage = pageNumber;
            this.searchCriteria = { ...this.searchCriteria, pageNumber };
            this.loadDocuments();
        },
        changeSort({ sortBy, sortDesc }) {
            const apiFields = { name: 'name', createdAt: 'created_at' };
            if (!apiFields[sortBy]) return;
            this.currentPage = 1;
            this.searchCriteria = {
                ...this.searchCriteria,
                pageNumber: 1,
                sortBy: apiFields[sortBy],
                orderBy: sortDesc ? 'desc' : 'asc',
            };
            this.loadDocuments();
        },
        documentTarget(document) {
            return document.isInternal ? document.fileUrl : (document.link || document.fileUrl);
        },
        openForm(document = null) {
            this.document = { uuid: document?.uuid || '' };
            this.formVisible = true;
            this.$nextTick(() => this.$bvModal.show('document-form'));
        },
        closeForm() {
            this.$bvModal.hide('document-form');
            this.formVisible = false;
            this.document = { uuid: '' };
        },
        async confirmDelete(document) {
            const confirmed = await this.$bvModal.msgBoxConfirm(
                this.$t('label.confirmDeleteX', [document.name]),
                {
                    title: this.$t('label.confirmDeleteDocument'),
                    okTitle: this.$t('label.delete'),
                    okVariant: 'danger',
                    cancelTitle: this.$t('label.cancel'),
                    cancelVariant: 'outline-secondary',
                    centered: true,
                },
            );
            if (!confirmed) return;
            await this.deleteDocument({ uuid: document.uuid, filter: this.searchCriteria });
        },
    },
};
</script>

<style scoped>
::v-deep .document-name-column {
    width: 400px;
    min-width: 300px;
    max-width: 400px;
    white-space: normal;
    overflow-wrap: anywhere;
}

::v-deep .document-company-column {
    width: 200px;
    min-width: 100px;
    max-width: 200px;
    white-space: normal;
    overflow-wrap: anywhere;
}
</style>
